import type { SelectDropdownOption } from 'src/components/common/SelectDropdown';
import type { CoreEnumDTO } from 'src/api/core/core-enums/useCoreEnums';

export const CORE_ENUM_NONE_VALUE = '__core_enum_none__';

export function optionPreview(
  coreEnum: CoreEnumDTO,
  emptyLabel: string,
): string {
  const names = (coreEnum.options ?? [])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((option) => option.name)
    .filter((name): name is string => Boolean(name));
  return names.length > 0 ? names.join(', ') : emptyLabel;
}

export function buildCoreEnumPickerOptions({
  coreEnums,
  emptyOptionsLabel,
  allowNone,
  noneLabel,
  noneDisplay,
}: {
  coreEnums: readonly CoreEnumDTO[];
  emptyOptionsLabel: string;
  allowNone: boolean;
  noneLabel: string;
  noneDisplay: string;
}): SelectDropdownOption[] {
  const enumOptions = [...coreEnums]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((coreEnum) => {
      const preview = optionPreview(coreEnum, emptyOptionsLabel);
      return {
        id: coreEnum.id,
        label: `${coreEnum.name} — ${preview}`,
        display: coreEnum.name,
      };
    });
  if (!allowNone) return enumOptions;
  return [
    {
      id: CORE_ENUM_NONE_VALUE,
      label: noneLabel,
      display: noneDisplay,
    },
    ...enumOptions,
  ];
}

export function findSelectedCoreEnum(
  enums: readonly CoreEnumDTO[] | undefined,
  value: string | null,
): CoreEnumDTO | undefined {
  if (value == null) return undefined;
  return enums?.find((coreEnum) => coreEnum.id === value);
}

export function resolveSelectedCoreEnumOption(
  options: readonly SelectDropdownOption[],
  value: string | null,
  allowNone: boolean,
): SelectDropdownOption | null {
  if (value == null) {
    return allowNone
      ? (options.find((option) => option.id === CORE_ENUM_NONE_VALUE) ?? null)
      : null;
  }
  return options.find((option) => option.id === value) ?? null;
}
